import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Row, Col, Statistic, Descriptions,
    message, Popconfirm, Typography, Tabs, Empty, Modal, Input, Form, Spin
} from 'antd';
import {
    ArrowLeftOutlined, UserOutlined, UsergroupAddOutlined, CheckCircleOutlined,
    ClockCircleOutlined, StopOutlined, CopyOutlined, FlagOutlined, TeamOutlined,
    CarOutlined, UserAddOutlined, EyeOutlined, LinkOutlined, DeleteOutlined,
    SearchOutlined, PhoneOutlined
} from '@ant-design/icons';
import ambassadorsService from '../../common/services/ambassadors.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const DetailAmbassadorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Modal pour lier un affilié
    const [linkAffiliateModalVisible, setLinkAffiliateModalVisible] = useState(false);
    const [availableAffiliates, setAvailableAffiliates] = useState([]);
    const [affiliatesLoading, setAffiliatesLoading] = useState(false);
    const [affiliateSearchTerm, setAffiliateSearchTerm] = useState('');
    const [linking, setLinking] = useState(false);

    // Modal pour créer un chauffeur
    const [createDriverModalVisible, setCreateDriverModalVisible] = useState(false);
    const [creating, setCreating] = useState(false);

    // Modal pour créer un affilié
    const [createAffiliateModalVisible, setCreateAffiliateModalVisible] = useState(false);
    const [affiliateForm] = Form.useForm();
    const [creatingAffiliate, setCreatingAffiliate] = useState(false);

    // Fetch ambassador data
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await ambassadorsService.getAmbassadorById(id);
            if (response.success) {
                setData(response.data);
            } else {
                message.error('Ambassadeur non trouve');
                navigate('/dashboard/ambassadors');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        message.success('Code copie !');
    };

    // Actions
    const handleSuspend = async () => {
        setActionLoading(true);
        try {
            const response = await ambassadorsService.suspendAmbassador(id);
            if (response.success) {
                message.success('Ambassadeur suspendu');
                fetchData();
            }
        } catch (error) {
            message.error('Erreur lors de la suspension');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReactivate = async () => {
        setActionLoading(true);
        try {
            const response = await ambassadorsService.reactivateAmbassador(id);
            if (response.success) {
                message.success('Ambassadeur reactive');
                fetchData();
            }
        } catch (error) {
            message.error('Erreur lors de la reactivation');
        } finally {
            setActionLoading(false);
        }
    };

    // Recherche des affiliés disponibles
    const searchAvailableAffiliates = useCallback(async (search = '') => {
        setAffiliatesLoading(true);
        try {
            const response = await ambassadorsService.getAvailableAffiliates({
                search,
                limit: 20
            });
            if (response.success) {
                setAvailableAffiliates(response.data || []);
            }
        } catch (error) {
            console.error('Erreur recherche affiliés:', error);
            message.error('Erreur lors de la recherche des affiliés');
        } finally {
            setAffiliatesLoading(false);
        }
    }, []);

    // Ouvrir le modal de liaison affilié
    const openLinkAffiliateModal = () => {
        setLinkAffiliateModalVisible(true);
        setAffiliateSearchTerm('');
        searchAvailableAffiliates('');
    };

    // Ouvrir le modal de création chauffeur
    const openCreateDriverModal = () => {
        form.resetFields();
        setCreateDriverModalVisible(true);
    };

    // Recherche avec debounce pour affiliés
    useEffect(() => {
        if (linkAffiliateModalVisible) {
            const timer = setTimeout(() => {
                searchAvailableAffiliates(affiliateSearchTerm);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [affiliateSearchTerm, linkAffiliateModalVisible, searchAvailableAffiliates]);

    // Lier un affilié à l'ambassadeur
    const handleLinkAffiliate = async (affiliateId) => {
        setLinking(true);
        try {
            const response = await ambassadorsService.linkAffiliate(id, affiliateId);
            if (response.success) {
                message.success(response.message || 'Affilié lié avec succès');
                setLinkAffiliateModalVisible(false);
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la liaison');
            }
        } catch (error) {
            console.error('Erreur liaison:', error);
            message.error(error.response?.data?.message || 'Erreur lors de la liaison de l\'affilié');
        } finally {
            setLinking(false);
        }
    };

    // Délier un affilié de l'ambassadeur
    const handleUnlinkAffiliate = async (affiliateId) => {
        try {
            const response = await ambassadorsService.unlinkAffiliate(id, affiliateId);
            if (response.success) {
                message.success('Affilié délié avec succès');
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la suppression de la liaison');
            }
        } catch (error) {
            console.error('Erreur déliaison:', error);
            message.error(error.response?.data?.message || 'Erreur lors de la déliaison');
        }
    };

    // Créer un nouveau chauffeur
    const handleCreateDriver = async (values) => {
        setCreating(true);
        try {
            // Ajouter le préfixe +225 au numéro de téléphone
            const dataToSend = {
                ...values,
                phone: `+225${values.phone.replace(/\s/g, '')}`
            };
            const response = await ambassadorsService.createDriverForAmbassador(id, dataToSend);
            if (response.success) {
                message.success(
                    <span>
                        Chauffeur <strong>{response.data.driver.firstName} {response.data.driver.lastName}</strong> créé avec succès !
                        <br />
                        <small>Mot de passe par défaut : <code>123456</code> | Points : 2000</small>
                    </span>,
                    5
                );
                setCreateDriverModalVisible(false);
                form.resetFields();
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la création');
            }
        } catch (error) {
            console.error('Erreur création:', error);
            message.error(error.response?.data?.message || 'Erreur lors de la création du chauffeur');
        } finally {
            setCreating(false);
        }
    };

    // Ouvrir le modal de création affilié
    const openCreateAffiliateModal = () => {
        affiliateForm.resetFields();
        setCreateAffiliateModalVisible(true);
    };

    // Créer un nouvel affilié
    const handleCreateAffiliate = async (values) => {
        setCreatingAffiliate(true);
        try {
            // Ajouter le préfixe +225 au numéro de téléphone
            const dataToSend = {
                ...values,
                phone: `+225${values.phone.replace(/\s/g, '')}`
            };
            const response = await ambassadorsService.createAffiliateForAmbassador(id, dataToSend);
            if (response.success) {
                message.success(
                    <span>
                        Affilié <strong>{response.data.affiliate.firstName} {response.data.affiliate.lastName}</strong> créé avec succès !
                        <br />
                        <small>Code: <code>{response.data.affiliate.referralCode}</code> | Mot de passe : <code>123456</code></small>
                    </span>,
                    5
                );
                setCreateAffiliateModalVisible(false);
                affiliateForm.resetFields();
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la création');
            }
        } catch (error) {
            console.error('Erreur création affilié:', error);
            message.error(error.response?.data?.message || 'Erreur lors de la création de l\'affilié');
        } finally {
            setCreatingAffiliate(false);
        }
    };

    // Affiliate columns
    const affiliateColumns = [
        {
            title: 'Affilie',
            key: 'affiliate',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{record.phone}</div>
                    </div>
                </Space>
            )
        },
        {
            title: 'Code',
            key: 'code',
            render: (_, record) => (
                <Tag color="gold">{record.affiliateProfile?.referralCode || '-'}</Tag>
            )
        },
        {
            title: 'Chauffeurs recrutes',
            key: 'drivers',
            render: (_, record) => (
                <Tag color="blue">{record.affiliateProfile?.stats?.totalReferred || 0}</Tag>
            )
        },
        {
            title: 'Ville',
            key: 'city',
            render: (_, record) => (
                <span>{record.city?.name || '-'}</span>
            )
        },
        {
            title: 'Statut',
            key: 'status',
            render: (_, record) => (
                <Tag color={record.affiliateProfile?.status === 'approved' ? 'success' : 'warning'}>
                    {record.affiliateProfile?.status === 'approved' ? 'Actif' : 'En attente'}
                </Tag>
            )
        },
        {
            title: 'Date creation',
            key: 'date',
            render: (_, record) => (
                <span style={{ fontSize: '12px' }}>
                    {dayjs(record.createdAt).format('DD/MM/YYYY')}
                </span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/dashboard/affiliates/${record._id}`)}
                    >
                        Detail
                    </Button>
                    <Popconfirm
                        title="Délier cet affilié de l'ambassadeur ?"
                        description="L'affilié ne sera plus associé à cet ambassadeur."
                        onConfirm={() => handleUnlinkAffiliate(record._id)}
                        okText="Oui, délier"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    // Driver columns - for direct drivers
    const directDriverColumns = [
        {
            title: 'Chauffeur',
            key: 'driver',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{record.phone}</div>
                    </div>
                </Space>
            )
        },
        {
            title: 'Statut',
            key: 'isActive',
            render: (_, record) => (
                <Tag color={record.isActive !== false ? 'success' : 'error'}>
                    {record.isActive !== false ? 'Actif' : 'Inactif'}
                </Tag>
            )
        },
        {
            title: 'Date inscription',
            key: 'date',
            render: (_, record) => (
                <span style={{ fontSize: '12px' }}>
                    {dayjs(record.createdAt).format('DD/MM/YYYY')}
                </span>
            )
        }
    ];

    // Driver columns - for affiliate drivers
    const affiliateDriverColumns = [
        {
            title: 'Chauffeur',
            key: 'driver',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{record.phone}</div>
                    </div>
                </Space>
            )
        },
        {
            title: 'Affilie',
            key: 'affiliate',
            render: (_, record) => {
                // Essayer referredByName d'abord, sinon affiliateId
                const affiliateName = record.referredBy?.referredByName ||
                    (record.referredBy?.affiliateId?.firstName
                        ? `${record.referredBy.affiliateId.firstName} ${record.referredBy.affiliateId.lastName}`
                        : null);
                return affiliateName ? (
                    <Tag color="cyan">{affiliateName}</Tag>
                ) : '-';
            }
        },
        {
            title: 'Statut',
            key: 'isActive',
            render: (_, record) => (
                <Tag color={record.isActive !== false ? 'success' : 'error'}>
                    {record.isActive !== false ? 'Actif' : 'Inactif'}
                </Tag>
            )
        },
        {
            title: 'Date inscription',
            key: 'date',
            render: (_, record) => (
                <span style={{ fontSize: '12px' }}>
                    {dayjs(record.createdAt).format('DD/MM/YYYY')}
                </span>
            )
        }
    ];

    if (loading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Text>Chargement...</Text>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ padding: '24px' }}>
                <Empty description="Ambassadeur non trouve" />
            </div>
        );
    }

    const ambassador = data.ambassador;
    const affiliates = data.affiliates || [];
    const drivers = data.drivers || [];
    const directDrivers = drivers.filter(d => d.source === 'direct');
    const affiliateDrivers = drivers.filter(d => d.source !== 'direct');
    const stats = data.stats || {};
    const status = ambassador.ambassadorProfile?.status;

    const statusConfig = {
        approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Actif' },
        pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'En attente' },
        suspended: { color: 'error', icon: <StopOutlined />, text: 'Suspendu' }
    };
    const currentStatus = statusConfig[status] || statusConfig.pending;

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/ambassadors')}
                    >
                        Retour
                    </Button>
                    <Title level={3} style={{ margin: 0 }}>
                        <FlagOutlined style={{ marginRight: '12px' }} />
                        Detail Ambassadeur
                    </Title>
                </Space>
                <Space>
                    {status === 'approved' && (
                        <Popconfirm
                            title="Suspendre cet ambassadeur ?"
                            onConfirm={handleSuspend}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <Button danger loading={actionLoading}>Suspendre</Button>
                        </Popconfirm>
                    )}
                    {status === 'suspended' && (
                        <Button
                            type="primary"
                            onClick={handleReactivate}
                            loading={actionLoading}
                        >
                            Reactiver
                        </Button>
                    )}
                </Space>
            </div>

            {/* Info Card */}
            <Card style={{ marginBottom: '24px' }}>
                <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }}>
                    <Descriptions.Item label="Nom complet">
                        {ambassador.firstName} {ambassador.lastName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Telephone">{ambassador.phone}</Descriptions.Item>
                    <Descriptions.Item label="Email">{ambassador.email || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Ville">
                        {ambassador.city?.name || ambassador.ambassadorProfile?.city || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Statut">
                        <Tag color={currentStatus.color} icon={currentStatus.icon}>
                            {currentStatus.text}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Code Ambassadeur">
                        {ambassador.ambassadorProfile?.ambassadorCode ? (
                            <Space>
                                <Tag color="gold" style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                                    {ambassador.ambassadorProfile.ambassadorCode}
                                </Tag>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => copyToClipboard(ambassador.ambassadorProfile.ambassadorCode)}
                                />
                            </Space>
                        ) : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Date d'approbation" span={3}>
                        {ambassador.ambassadorProfile?.approvedAt
                            ? dayjs(ambassador.ambassadorProfile.approvedAt).format('DD/MM/YYYY HH:mm')
                            : '-'}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Total Affilies"
                            value={stats.totalAffiliates || affiliates.length || 0}
                            prefix={<UsergroupAddOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Affilies Actifs"
                            value={stats.activeAffiliates || 0}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Total Chauffeurs"
                            value={stats.totalDrivers || drivers.length || 0}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Chauffeurs Actifs"
                            value={stats.activeDrivers || 0}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Tabs */}
            <Card>
                <Tabs defaultActiveKey="affiliates">
                    <TabPane
                        tab={<span><UsergroupAddOutlined /> Affilies ({affiliates.length})</span>}
                        key="affiliates"
                    >
                        {/* Boutons d'action pour affiliés */}
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <Button
                                icon={<LinkOutlined />}
                                onClick={openLinkAffiliateModal}
                                style={{ borderRadius: 8 }}
                            >
                                Lier un affilié existant
                            </Button>
                            <Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                onClick={openCreateAffiliateModal}
                                style={{
                                    borderRadius: 8,
                                    background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
                                    border: 'none'
                                }}
                            >
                                Créer un affilié
                            </Button>
                        </div>
                        <Table
                            columns={affiliateColumns}
                            dataSource={affiliates}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            locale={{ emptyText: 'Aucun affilie lié à cet ambassadeur' }}
                        />
                    </TabPane>
                    <TabPane
                        tab={<span><CarOutlined /> Chauffeurs directs ({directDrivers.length})</span>}
                        key="directDrivers"
                    >
                        {/* Bouton d'action pour créer un chauffeur */}
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                onClick={openCreateDriverModal}
                                style={{
                                    borderRadius: 8,
                                    background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                                    border: 'none'
                                }}
                            >
                                Créer un chauffeur
                            </Button>
                        </div>
                        <Table
                            columns={directDriverColumns}
                            dataSource={directDrivers}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            locale={{ emptyText: 'Aucun chauffeur enregistré directement par l\'ambassadeur' }}
                        />
                    </TabPane>
                    <TabPane
                        tab={<span><TeamOutlined /> Chauffeurs via affilies ({affiliateDrivers.length})</span>}
                        key="affiliateDrivers"
                    >
                        <Table
                            columns={affiliateDriverColumns}
                            dataSource={affiliateDrivers}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            locale={{ emptyText: 'Aucun chauffeur recrute par les affilies' }}
                        />
                    </TabPane>
                </Tabs>
            </Card>

            {/* Modal pour lier un affilié existant */}
            <Modal
                title={
                    <Space>
                        <LinkOutlined style={{ color: '#1890ff' }} />
                        <span>Lier un affilié à {data?.ambassador?.firstName} {data?.ambassador?.lastName}</span>
                    </Space>
                }
                open={linkAffiliateModalVisible}
                onCancel={() => setLinkAffiliateModalVisible(false)}
                footer={null}
                width={700}
            >
                <div style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Rechercher par nom ou téléphone..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={affiliateSearchTerm}
                        onChange={(e) => setAffiliateSearchTerm(e.target.value)}
                        allowClear
                        size="large"
                        style={{ borderRadius: 8 }}
                    />
                </div>

                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Sélectionnez un affilié à lier à cet ambassadeur. Seuls les affiliés non liés à un ambassadeur sont affichés.
                </Text>

                {affiliatesLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin tip="Recherche des affiliés..." />
                    </div>
                ) : (
                    <Table
                        columns={[
                            {
                                title: 'Affilié',
                                key: 'name',
                                render: (_, record) => (
                                    <div>
                                        <div style={{ fontWeight: 500 }}>
                                            {record.firstName || ''} {record.lastName || ''}
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Code: {record.affiliateProfile?.referralCode || '-'}
                                        </Text>
                                    </div>
                                )
                            },
                            {
                                title: 'Téléphone',
                                dataIndex: 'phone',
                                key: 'phone',
                                render: (phone) => <Tag color="blue">{phone}</Tag>
                            },
                            {
                                title: 'Action',
                                key: 'action',
                                width: 120,
                                render: (_, record) => (
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<LinkOutlined />}
                                        loading={linking}
                                        onClick={() => handleLinkAffiliate(record._id)}
                                        style={{ borderRadius: 6 }}
                                    >
                                        Lier
                                    </Button>
                                )
                            }
                        ]}
                        dataSource={availableAffiliates}
                        rowKey="_id"
                        pagination={{ pageSize: 5 }}
                        locale={{ emptyText: 'Aucun affilié disponible trouvé' }}
                        size="small"
                    />
                )}
            </Modal>

            {/* Modal pour créer un nouveau chauffeur */}
            <Modal
                title={
                    <Space>
                        <UserAddOutlined style={{ color: '#52c41a' }} />
                        <span>Créer un chauffeur pour {data?.ambassador?.firstName} {data?.ambassador?.lastName}</span>
                    </Space>
                }
                open={createDriverModalVisible}
                onCancel={() => setCreateDriverModalVisible(false)}
                footer={null}
                width={500}
            >
                <div style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 20
                }}>
                    <Text type="secondary">
                        Le chauffeur sera directement rattaché à cet ambassadeur avec :
                    </Text>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                        <li><strong>2000 points</strong> de bienvenue</li>
                        <li>Mot de passe par défaut : <code>123456</code></li>
                        <li>Commission directe pour l'ambassadeur</li>
                    </ul>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateDriver}
                >
                    <Form.Item
                        name="lastName"
                        label="Nom"
                        rules={[{ required: true, message: 'Le nom est requis' }]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Ex: Dupont"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="firstName"
                        label="Prénom"
                        rules={[{ required: true, message: 'Le prénom est requis' }]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Ex: Jean"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label="Téléphone"
                        rules={[
                            { required: true, message: 'Le téléphone est requis' },
                            { pattern: /^[\d\s]{8,}$/, message: 'Numéro de téléphone invalide (10 chiffres)' }
                        ]}
                    >
                        <Input
                            addonBefore="+225"
                            placeholder="07 00 00 00 00"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="driverProfilId"
                        label="ID Profil Yandex (optionnel)"
                    >
                        <Input
                            placeholder="Ex: abc123def456..."
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                        <Row justify="end" gutter={12}>
                            <Col>
                                <Button onClick={() => setCreateDriverModalVisible(false)}>
                                    Annuler
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={creating}
                                    icon={<UserAddOutlined />}
                                    style={{
                                        borderRadius: 8,
                                        background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                                        border: 'none'
                                    }}
                                >
                                    Créer le chauffeur
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal pour créer un nouvel affilié */}
            <Modal
                title={
                    <Space>
                        <UserAddOutlined style={{ color: '#1890ff' }} />
                        <span>Créer un affilié pour {data?.ambassador?.firstName} {data?.ambassador?.lastName}</span>
                    </Space>
                }
                open={createAffiliateModalVisible}
                onCancel={() => setCreateAffiliateModalVisible(false)}
                footer={null}
                width={500}
            >
                <div style={{
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 20
                }}>
                    <Text type="secondary">
                        L'affilié sera directement rattaché à cet ambassadeur avec :
                    </Text>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                        <li>Code de parrainage <strong>TIGOON-XXXXXX</strong> unique</li>
                        <li>Mot de passe par défaut : <code>123456</code></li>
                        <li>Statut : <Tag color="success">Approuvé</Tag></li>
                    </ul>
                </div>

                <Form
                    form={affiliateForm}
                    layout="vertical"
                    onFinish={handleCreateAffiliate}
                >
                    <Form.Item
                        name="lastName"
                        label="Nom"
                        rules={[{ required: true, message: 'Le nom est requis' }]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Ex: Dupont"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="firstName"
                        label="Prénom"
                        rules={[{ required: true, message: 'Le prénom est requis' }]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Ex: Jean"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label="Téléphone"
                        rules={[
                            { required: true, message: 'Le téléphone est requis' },
                            { pattern: /^[\d\s]{8,}$/, message: 'Numéro de téléphone invalide (10 chiffres)' }
                        ]}
                    >
                        <Input
                            addonBefore="+225"
                            placeholder="07 00 00 00 00"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email (optionnel)"
                    >
                        <Input
                            placeholder="Ex: jean.dupont@email.com"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                        <Row justify="end" gutter={12}>
                            <Col>
                                <Button onClick={() => setCreateAffiliateModalVisible(false)}>
                                    Annuler
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={creatingAffiliate}
                                    icon={<UserAddOutlined />}
                                    style={{
                                        borderRadius: 8,
                                        background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
                                        border: 'none'
                                    }}
                                >
                                    Créer l'affilié
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DetailAmbassadorPage;
